import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axios-auth';
import { normalizeApiResponse, sanitizeApiPayload } from '../utils/api-helpers';
// Helper function to process API responses consistently
const processApiResponse = (response) => {
    const userData = normalizeApiResponse(response);
    return sanitizeApiPayload(userData, {
        id: '',
        email: '',
        first_name: '',
        last_name: '',
        role: 'student',
        status: 'active',
        settings: {}
    });
};
// Helper function to check if user is admin
export const isUserAdmin = (user) => {
    return user?.role === 'admin' || !!user?.is_admin;
};
// Async thunk for updating student settings
export const update_student_settings = createAsyncThunk('user/updateStudentSettings', async (settings, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.patch('/students/settings', settings);
        return processApiResponse(response);
    }
    catch (error) {
        return rejectWithValue({
            message: error.response?.data?.message || error.message || 'An error occurred'
        });
    }
});
// Async thunk for updating tutor settings
export const update_tutor_settings = createAsyncThunk('user/updateTutorSettings', async (settings, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.put('/tutors/settings', settings);
        return processApiResponse(response);
    }
    catch (error) {
        console.error('Tutor settings update error:', error);
        return rejectWithValue({
            message: error.response?.data?.message || error.message || 'An error occurred'
        });
    }
});
// Fetch user profile
export const fetch_current_user = createAsyncThunk('user/fetchCurrentUser', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('/user/current');
        // Get user's admin status by checking role
        const userData = processApiResponse(response);
        // Check if user is an admin using API call if needed
        // This could be extended to make a separate API call for admin verification
        // if the data isn't part of the standard user data
        return userData;
    }
    catch (error) {
        return rejectWithValue({
            message: error.response?.data?.message || error.message || 'An error occurred'
        });
    }
});
// Add a new thunk for updating tutor addresses separately
export const update_tutor_address = createAsyncThunk('users/updateTutorAddress', async (addressData, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/tutors/address', addressData);
        return response.data;
    }
    catch (error) {
        return rejectWithValue(error.response?.data || { success: false, message: error.message || 'An error occurred' });
    }
});
const user_slice = createSlice({
    name: 'user',
    initialState: {
        data: null,
        is_loading: false,
        error: null,
    },
    reducers: {
        clear_user: (state) => {
            state.data = null;
            state.error = null;
        },
        set_admin_status: (state, action) => {
            if (state.data) {
                state.data.is_admin = action.payload;
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch current user cases
            .addCase(fetch_current_user.pending, (state) => {
            state.is_loading = true;
            state.error = null;
        })
            .addCase(fetch_current_user.fulfilled, (state, action) => {
            state.is_loading = false;
            state.data = action.payload;
            // Set admin status based on role
            state.data.is_admin = action.payload.role === 'admin';
        })
            .addCase(fetch_current_user.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.payload.message || 'Failed to fetch user';
        })
            // Update student settings cases
            .addCase(update_student_settings.pending, (state) => {
            state.is_loading = true;
            state.error = null;
        })
            .addCase(update_student_settings.fulfilled, (state, action) => {
            state.is_loading = false;
            state.data = action.payload;
            // Preserve admin status
            if (state.data && state.data.is_admin !== undefined) {
                state.data.is_admin = action.payload.role === 'admin' || state.data.is_admin;
            }
        })
            .addCase(update_student_settings.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.payload.message || 'Failed to update student settings';
        })
            // Update tutor settings cases
            .addCase(update_tutor_settings.pending, (state) => {
            state.is_loading = true;
            state.error = null;
        })
            .addCase(update_tutor_settings.fulfilled, (state, action) => {
            state.is_loading = false;
            state.data = action.payload;
            // Preserve admin status
            if (state.data && state.data.is_admin !== undefined) {
                state.data.is_admin = action.payload.role === 'admin' || state.data.is_admin;
            }
        })
            .addCase(update_tutor_settings.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.payload.message || 'Failed to update tutor settings';
        });
    },
});
export const { clear_user, set_admin_status } = user_slice.actions;
export default user_slice.reducer;
